import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, API_BASE, detailText } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Users, Circle, Clock, KeyRound, Copy, Pencil, Trash2, Star, Search, RefreshCw, MessageSquare, BarChart2, TrendingUp, Zap, ShieldCheck, Activity, Sparkles, LifeBuoy, Bug, Lightbulb, HelpCircle, CheckCircle2 } from "lucide-react";
import { useT } from "@/context/LanguageContext";

export default function Admin() {
  const { t } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  // Tab dikendalikan URL (/admin/<section>) supaya bisa jadi sub-menu sidebar.
  const activeTab = location.pathname.split("/")[2] || "pending";
  const [pending, setPending] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [resettingUserId, setResettingUserId] = useState(null);
  // Hasil reset password untuk ditampilkan di dialog beserta tombol copy
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  // Masa aktif akun: 1 tahun aktif -> 3 bulan tenggang -> arsip -> hapus.
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountBusyId, setAccountBusyId] = useState(null);

  // Pilihan paket saat approve user (user_id -> 'trial' | 'premium')
  const [selectedPlans, setSelectedPlans] = useState({});

  // Pembayaran Premium (checkout manual / QRIS)
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [verifyingPayId, setVerifyingPayId] = useState(null);

  // Bank kutipan harian (ditampilkan ke semua user lewat /dashboard)
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quoteText, setQuoteText] = useState("");
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteEditId, setQuoteEditId] = useState(null);

  const loadQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data.quotes || []);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat bank kutipan");
    } finally { setLoadingQuotes(false); }
  };

  // Library musik global untuk Undangan Digital. Semua pasangan memilih
  // dari sini; tiap pasangan juga bisa mengunggah lagunya sendiri di editor.
  const [tracks, setTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [trackForm, setTrackForm] = useState({ title: "", artist: "", credit: "", license: "CC BY 4.0" });
  const [trackFile, setTrackFile] = useState(null);
  const [trackBusy, setTrackBusy] = useState(false);
  // Object URL untuk pratinjau. Dibuat ulang hanya saat file berubah dan
  // dicabut saat berganti/unmount - memanggil `URL.createObjectURL` langsung
  // di dalam render akan membocorkan URL baru setiap kali komponen
  // render ulang, dan browser tidak pernah melepaskannya.
  const [trackPratinjauUrl, setTrackPratinjauUrl] = useState("");

  useEffect(() => {
    if (!trackFile) {
      setTrackPratinjauUrl("");
      return;
    }
    const url = URL.createObjectURL(trackFile);
    setTrackPratinjauUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [trackFile]);

  const loadTracks = async () => {
    setLoadingTracks(true);
    try {
      // `GET /api/tracks` (bukan `/admin/tracks`) yang mengembalikan daftar
      // metadata. Endpoint `/admin/tracks` hanya menerima POST/PATCH/DELETE -
      // memanggilnya dengan GET berakhir 405 dan tab tidak pernah terisi.
      const { data } = await api.get("/tracks");
      setTracks(data.tracks || []);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat library musik");
    } finally { setLoadingTracks(false); }
  };

  const resetTrackForm = () => {
    setTrackForm({ title: "", artist: "", credit: "", license: "CC BY 4.0" });
    setTrackFile(null);
  };

  const saveTrack = async () => {
    if (!trackFile) return toast.error("Pilih file audio dulu");
    if (!trackForm.title.trim()) return toast.error("Judul lagu wajib diisi");
    if (!trackForm.credit.trim()) {
      return toast.error("Kredit wajib diisi -lisensi Creative Commons mewajibkannya");
    }
    if (!trackForm.license.trim()) return toast.error("Jenis lisensi wajib diisi");

    setTrackBusy(true);
    try {
      const form = new FormData();
      form.append("file", trackFile);
      form.append("title", trackForm.title.trim());
      form.append("artist", trackForm.artist.trim());
      form.append("credit", trackForm.credit.trim());
      form.append("license", trackForm.license.trim());
      await api.post("/admin/tracks", form);
      toast.success("Lagu ditambahkan ke library");
      resetTrackForm();
      loadTracks();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal mengunggah lagu");
    } finally { setTrackBusy(false); }
  };

  const deleteTrack = async (trackId) => {
    if (!window.confirm("Hapus lagu ini dari library?")) return;
    try {
      await api.delete(`/admin/tracks/${trackId}`);
      toast.success("Lagu dihapus");
      loadTracks();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus lagu");
    }
  };

  const setTrackField = (key, value) => setTrackForm((f) => ({ ...f, [key]: value }));

  const resetQuoteForm = () => {
    setQuoteText("");
    setQuoteEditId(null);
  };

  const saveQuote = async () => {
    const text = quoteText.trim();
    if (!text) return toast.error("Kutipan wajib diisi");
    setQuoteBusy(true);
    try {
      if (quoteEditId) {
        await api.patch(`/admin/quotes/${quoteEditId}`, { text });
        toast.success("Kutipan diperbarui");
      } else {
        await api.post("/admin/quotes", { text });
        toast.success("Kutipan ditambahkan");
      }
      resetQuoteForm();
      await loadQuotes();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menyimpan kutipan");
    } finally { setQuoteBusy(false); }
  };

  const removeQuote = async (q) => {
    if (!window.confirm("Hapus kutipan ini dari bank?\n\nKutipan akan langsung hilang dari bank.")) return;
    try {
      await api.delete(`/admin/quotes/${q.quote_id}`);
      toast.success("Kutipan dihapus");
      if (quoteEditId === q.quote_id) resetQuoteForm();
      await loadQuotes();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus kutipan");
    }
  };

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const { data } = await api.get("/admin/accounts/lifecycle");
      setAccounts(data.users || []);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat data masa aktif");
    } finally { setLoadingAccounts(false); }
  };

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const { data } = await api.get("/admin/payments");
      setPayments(data.payments || []);
    } catch (e) {
      console.error("Gagal memuat pembayaran", e);
    } finally { setLoadingPayments(false); }
  };

  const handleVerifyPayment = async (payId, action) => {
    setVerifyingPayId(payId);
    try {
      await api.post(`/admin/payments/${payId}/verify`, { action });
      toast.success(action === "approve" ? "Pembayaran disetujui & masa aktif akun diperpanjang! ✨" : "Pembayaran ditolak");
      loadPayments();
      loadAccounts();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memproses pembayaran");
    } finally {
      setVerifyingPayId(null);
    }
  };

  const upgradeToPremium = async (user) => {
    const ok = window.confirm(
      `Upgrade akun ${user.name || user.phone} ke Paket Premium?\n\nAkun akan menjadi Premium dengan masa aktif 1 Tahun Penuh (365 hari) dan notifikasi WA akan dikirim jika terhubung.`
    );
    if (!ok) return;
    setAccountBusyId(user.user_id);
    try {
      const { data } = await api.post(`/admin/accounts/${user.user_id}/upgrade-plan`, { plan_type: "premium", days: 365 });
      toast.success(
        data?.wa_sent
          ? `Akun ${user.name || "User"} berhasil di-upgrade ke Premium 1 Tahun ✨ · WA Terkirim`
          : `Akun ${user.name || "User"} berhasil di-upgrade ke Premium 1 Tahun ✨`
      );
      await loadAccounts();
      await loadActivity();
      await loadAnalytics();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal meng-upgrade akun ke Premium");
    } finally {
      setAccountBusyId(null);
    }
  };

  const extendAccount = async (user) => {
    setAccountBusyId(user.user_id);
    try {
      await api.post(`/admin/accounts/${user.user_id}/extend`, { days: 365 });
      toast.success(`Masa aktif ${user.name} diperpanjang 1 tahun`);
      await loadAccounts();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memperpanjang masa aktif");
    } finally { setAccountBusyId(null); }
  };

  const restoreAccount = async (user) => {
    const ok = window.confirm(
      `Pulihkan akun ${user.name}?\n\nAkun akan mendapat masa aktif baru 1 tahun dan tidak lagi diarsipkan.`
    );
    if (!ok) return;
    setAccountBusyId(user.user_id);
    try {
      await api.post(`/admin/accounts/${user.user_id}/restore`);
      toast.success(`Akun ${user.name} berhasil dipulihkan`);
      await loadAccounts();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memulihkan akun");
    } finally { setAccountBusyId(null); }
  };
  // WhatsApp service: status koneksi + QR pairing. QR Baileys hanya berlaku
  // singkat, jadi halaman admin polled selama belum terhubung.
  const [wa, setWa] = useState({ serviceUp: true, connected: false, phone: "", qr: null, error: "" });
  const [waConfirmLogout, setWaConfirmLogout] = useState(false);
  const [waBusy, setWaBusy] = useState(false);

  const loadWaStatus = async () => {
    try {
      const { data } = await api.get("/admin/wa/status");
      setWa((w) => ({ ...w, serviceUp: !!data.service_up, connected: !!data.connected, phone: data.phone || "", error: data.error || "" }));
    } catch (e) {
      setWa((w) => ({ ...w, serviceUp: false, error: detailText(e?.response?.data?.detail) || "Gagal menghubungi server" }));
    }
  };

  const loadWaQr = async () => {
    try {
      const { data } = await api.get("/admin/wa/qr");
      setWa((w) => ({
        ...w,
        serviceUp: !!data.service_up,
        connected: !!data.connected,
        qr: data.qr || null,
        error: data.error || "",
      }));
    } catch (e) {
      // 429 = rate limit, bukan service mati. Menandai serviceUp=false akan
      // menghentikan polling selamanya, jadi harus dibedakan.
      const status = e?.response?.status;
      const detail = detailText(e?.response?.data?.detail);
      setWa((w) => ({
        ...w,
        serviceUp: status === 429 ? w.serviceUp : false,
        error: status === 429 ? "Terlalu sering meminta QR, tunggu sebentar." : detail || "Gagal mengambil QR",
      }));
    }
  };

  const refreshWaQr = async () => {
    setWaBusy(true);
    try {
      await api.post("/admin/wa/refresh-qr");
      toast.success("Meminta kode QR baru...");
      setWa((w) => ({ ...w, qr: null }));
      setTimeout(() => {
        loadWaStatus();
        loadWaQr();
      }, 2500);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal membuat QR baru");
    } finally { setWaBusy(false); }
  };

  // Poll QR hanya selama belum terhubung supaya tidak sia-sia.
  // 5 detik = 12 request/menit, aman di bawah rate limit 30/menit.
  useEffect(() => {
    loadWaStatus();
    loadWaQr();
    const id = setInterval(() => {
      setWa((w) => {
        if (w.connected || !w.serviceUp) return w;
        loadWaQr();
        return w;
      });
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const waLogout = async () => {
    setWaBusy(true);
    try {
      await api.post("/admin/wa/logout");
      toast.success("Sesi WhatsApp diputus. Pindai ulang QR untuk connect.");
      setWaConfirmLogout(false);
      setWa((w) => ({ ...w, connected: false, phone: "", qr: null }));
      await loadWaStatus();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memutus sesi WhatsApp");
    } finally { setWaBusy(false); }
  };

  // Load Pending Users
  const loadPending = async () => {
    try {
      const { data } = await api.get("/admin/users/pending");
      setPending(data.users || []);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat data pendaftaran");
    }
  };

  // Load User Activity (Online Status)
  const loadActivity = async () => {
    setLoadingActivity(true);
    try {
      const { data } = await api.get("/admin/users/activity");
      setActivities(data.users || []);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat aktivitas user");
    } finally {
      setLoadingActivity(false);
    }
  };

  // Feedback & Rating User
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [feedbackStats, setFeedbackStats] = useState({ total_count: 0, avg_rating: 0, rating_counts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const [feedbackRatingFilter, setFeedbackRatingFilter] = useState("all");
  const [deletingFeedbackId, setDeletingFeedbackId] = useState(null);

  const loadFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const { data } = await api.get("/admin/feedbacks");
      setFeedbacks(data.feedbacks || []);
      setFeedbackStats({
        total_count: data.total_count || 0,
        avg_rating: data.avg_rating || 0,
        rating_counts: data.rating_counts || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      });
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat feedback user");
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus ulasan ini?")) return;
    setDeletingFeedbackId(feedbackId);
    try {
      await api.delete(`/admin/feedbacks/${feedbackId}`);
      toast.success("Ulasan berhasil dihapus");
      loadFeedbacks();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus feedback");
    } finally {
      setDeletingFeedbackId(null);
    }
  };

  // HELPDESK & LAPORAN TIKET (BUG, SARAN, BANTUAN)
  const [helpdeskTickets, setHelpdeskTickets] = useState([]);
  const [loadingHelpdesk, setLoadingHelpdesk] = useState(false);
  const [helpdeskCategoryFilter, setHelpdeskCategoryFilter] = useState("all");
  const [helpdeskStatusFilter, setHelpdeskStatusFilter] = useState("all");
  const [helpdeskSearch, setHelpdeskSearch] = useState("");
  const [helpdeskStats, setHelpdeskStats] = useState({ total: 0, category_counts: { bug: 0, suggestion: 0, help: 0 }, status_counts: { open: 0, in_progress: 0, resolved: 0 } });
  const [updatingTicketId, setUpdatingTicketId] = useState(null);

  const loadHelpdeskTickets = async () => {
    setLoadingHelpdesk(true);
    try {
      const { data } = await api.get("/admin/helpdesk/tickets");
      setHelpdeskTickets(data.tickets || []);
      setHelpdeskStats({
        total: data.total || 0,
        category_counts: data.category_counts || { bug: 0, suggestion: 0, help: 0 },
        status_counts: data.status_counts || { open: 0, in_progress: 0, resolved: 0 },
      });
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat tiket helpdesk");
    } finally {
      setLoadingHelpdesk(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus, currentNote = "") => {
    setUpdatingTicketId(ticketId);
    try {
      await api.patch(`/admin/helpdesk/tickets/${ticketId}`, {
        status: newStatus,
        admin_note: currentNote,
      });
      toast.success(`Status tiket diubah menjadi ${newStatus}`);
      loadHelpdeskTickets();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memperbarui status tiket");
    } finally {
      setUpdatingTicketId(null);
    }
  };

  const deleteHelpdeskTicket = async (ticketId) => {
    if (!window.confirm("Hapus tiket laporan ini?")) return;
    try {
      await api.delete(`/admin/helpdesk/tickets/${ticketId}`);
      toast.success("Tiket berhasil dihapus");
      loadHelpdeskTickets();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus tiket");
    }
  };

  // Analitik Aktivitas User
  const [analyticsPeriod, setAnalyticsPeriod] = useState("today");
  const [analytics, setAnalytics] = useState({
    summary: {
      total_accounts: 0,
      approved_count: 0,
      pending_count: 0,
      admin_count: 0,
      premium_count: 0,
      trial_count: 0,
      online_realtime: 0,
      daily_active_users: 0,
      weekly_active_users: 0,
      avg_spend_time_minutes: 0,
      peak_activity_hour: "-"
    },
    hourly_activity: [],
    period: "today",
    date_label: "",
    timezone: "WIB (UTC+7)"
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const loadAnalytics = useCallback(async (period = analyticsPeriod) => {
    setLoadingAnalytics(true);
    try {
      const { data } = await api.get(`/admin/analytics?period=${period}`);
      if (data) setAnalytics(data);
    } catch (e) {
      console.error("Gagal memuat analitik user", e);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [analyticsPeriod]);

  const handlePeriodChange = (p) => {
    setAnalyticsPeriod(p);
    loadAnalytics(p);
  };

  // System Error Logs
  const [errorLogs, setErrorLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logSourceFilter, setLogSourceFilter] = useState("all");
  const [logSearch, setLogSearch] = useState("");

  const loadErrorLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get("/admin/logs/error");
      setErrorLogs(data.logs || []);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat log error");
    } finally {
      setLoadingLogs(false);
    }
  };

  const clearErrorLogs = async () => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus semua log error system?")) return;
    try {
      await api.delete("/admin/logs/error");
      toast.success("Semua log error berhasil dibersihkan");
      loadErrorLogs();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal membersihkan log error");
    }
  };

  useEffect(() => {
    loadPending();
    loadActivity();
    loadAccounts();
    loadPayments();
    loadQuotes();
    loadTracks();
    loadFeedbacks();
    loadHelpdeskTickets();
    loadAnalytics();
    loadErrorLogs();
  }, []);

  const [actionUserId, setActionUserId] = useState(null);

  // Approve
  const approve = async (user) => {
    setActionUserId(user.user_id);
    try {
      const planType = selectedPlans[user.user_id] || "trial";
      const { data } = await api.post(`/admin/users/${user.user_id}/approve`, { plan_type: planType });
      const planText = planType === "trial" ? "Trial 14 Hari" : "Premium 1 Tahun";
      toast.success(
        data?.wa_sent
          ? `${user.name} berhasil disetujui (${planText}) ✅ · WA terkirim`
          : `${user.name} berhasil disetujui (${planText}) ✅ · WA tidak terkirim`
      );
      await loadPending();
      await loadActivity();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menyetujui user");
    } finally {
      setActionUserId(null);
    }
  };

  // Reject
  const reject = async (user) => {
    setActionUserId(user.user_id);
    try {
      await api.post(`/admin/users/${user.user_id}/reject`);
      toast.success(`${user.name} berhasil ditolak ❌`);
      await loadPending();
      await loadActivity();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menolak user");
    } finally {
      setActionUserId(null);
    }
  };

  // Reset Password (Fitur Baru)
  const handleResetPassword = async (user) => {
    // Konfirmasi ke admin
    const isManual = window.confirm(`Reset password untuk ${user.name}?\n\nKlik "OK" untuk Generate Otomatis.`);
    if (!isManual) return;

    setResettingUserId(user.user_id);
    try {
      // Body kosong berarti backend akan generate otomatis
      const { data } = await api.post(`/admin/users/${user.user_id}/reset-password`, {});
      
      if (data.ok) {
        toast.success(`Password untuk ${user.name} berhasil di-reset!`);
        // Tampilkan password baru di dialog (bisa di-copy admin)
        setCopied(false);
        setResetResult({ name: user.name, phone: user.phone, password: data.new_password });
        await loadActivity(); // Refresh status (akan jadi offline karena sesi dihapus)
      }
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal mereset password user");
    } finally {
      setResettingUserId(null);
    }
  };

  // Salin password hasil generate ke clipboard
  const copyResetPassword = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      setCopied(true);
      toast.success("Password tersalin ke clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Gagal menyalin. Silakan salin manual.");
    }
  };

  // Helper format waktu
  const formatDate = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10 overflow-x-hidden">
      <div className="mb-6 md:mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Admin Panel
        </div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold">Manajemen Pengguna</h1>
        <p className="text-xs md:text-sm text-muted-foreground mt-1">
          Kelola pendaftaran &amp; pantau aktivitas pasangan.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => navigate(`/admin/${v}`)} className="w-full max-w-full">
        <TabsList className="mb-6 w-full max-w-full justify-start overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex-nowrap h-11 p-1">
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="pending" data-testid="admin-tab-pending">
            Pendaftaran ({pending.length})
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="analytics" data-testid="admin-tab-analytics">
            Analitik User
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="activity" data-testid="admin-tab-activity">
            Aktivitas Akun
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="whatsapp" data-testid="admin-tab-whatsapp">
            WhatsApp Service
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="accounts" data-testid="admin-tab-accounts">
            Masa Aktif
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="quotes" data-testid="admin-tab-quotes">
            Kutipan ({quotes.length})
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="music" data-testid="admin-tab-music">
            Musik ({tracks.length})
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="feedback" data-testid="admin-tab-feedback">
            Feedback ({feedbackStats.total_count})
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="helpdesk" data-testid="admin-tab-helpdesk">
            Helpdesk ({helpdeskStats.total})
          </TabsTrigger>
          <TabsTrigger className="shrink-0 px-3.5 py-1.5 text-xs sm:text-sm font-medium" value="logs" data-testid="admin-tab-logs">
            Log System ({errorLogs.length})
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: PENDING USERS */}
        <TabsContent value="pending">
          <Card className="rounded-2xl overflow-hidden">
            {pending.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground flex flex-col items-center gap-2">
                <Users className="h-8 w-8 opacity-20" />
                <span>Tidak ada pendaftaran yang menunggu persetujuan.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Nama</th>
                      <th className="text-left px-4 py-3">Nomor HP</th>
                      <th className="text-left px-4 py-3">Kode Undangan</th>
                      <th className="text-left px-4 py-3">Tanggal Daftar</th>
                      <th className="text-left px-4 py-3">Paket Aktivasi</th>
                      <th className="text-right px-4 py-3">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((u) => (
                      <tr key={u.user_id} className="border-t border-border">
                        <td className="px-4 py-3 font-medium">{u.name}</td>
                        <td className="px-4 py-3 font-mono">{u.phone}</td>
                        <td className="px-4 py-3 font-mono text-muted-foreground">
                          {u.pending_wedding_id || "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {(u.created_at || "").slice(0, 10)}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={selectedPlans[u.user_id] || "trial"}
                            onChange={(e) => setSelectedPlans((prev) => ({ ...prev, [u.user_id]: e.target.value }))}
                            className="border border-input bg-background text-xs rounded-md px-2.5 py-1 font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            data-testid={`plan-select-${u.user_id}`}
                          >
                            <option value="trial">Trial (14 Hari)</option>
                            <option value="premium">Premium (1 Tahun)</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => approve(u)}
                            disabled={actionUserId === u.user_id}
                            className="hover:bg-green-50 hover:text-green-600"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => reject(u)}
                            disabled={actionUserId === u.user_id}
                            className="hover:bg-red-50 hover:text-red-600"
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 2: USER ACTIVITY */}
        <TabsContent value="activity">
          <Card className="rounded-2xl overflow-hidden">
            <div className="p-4 bg-muted/30 border-b flex justify-between items-center">
              <span className="text-sm font-medium">Status Login Terkini</span>
              <Button variant="ghost" size="sm" onClick={loadActivity} disabled={loadingActivity}>
                <Clock className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>
            {loadingActivity ? (
              <div className="p-10 text-center text-muted-foreground">Memuat data...</div>
            ) : activities.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">Belum ada user yang terdaftar.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Nama</th>
                      <th className="text-left px-4 py-3">Nomor HP</th>
                      <th className="text-left px-4 py-3">Pasangan Terhubung</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Login Terakhir</th>
                      <th className="text-left px-4 py-3">Role</th>
                      {/* Kolom Aksi Baru */}
                      <th className="text-right px-4 py-3">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((u) => (
                      <tr key={u.user_id} className="border-t border-border">
                        <td className="px-4 py-3 font-medium">{u.name}</td>
                        <td className="px-4 py-3 font-mono">{u.phone}</td>
                        <td className="px-4 py-3">
                          {u.partner?.has_partner ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground text-xs flex items-center gap-1">
                                <span className="text-primary font-bold">💍</span> {u.partner.partner_name}
                              </span>
                              <span className="text-[11px] text-muted-foreground font-mono">
                                {u.partner.partner_phone}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground/60 italic">Belum terhubung</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {u.is_online ? (
                              <>
                                <Circle className="h-3 w-3 fill-green-500 text-green-500" />
                                <span className="text-green-600 font-medium">Online</span>
                              </>
                            ) : (
                              <>
                                <Circle className="h-3 w-3 fill-gray-300 text-gray-300" />
                                <span className="text-gray-400">Offline</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {u.last_login ? formatDate(u.last_login) : "-"}
                        </td>
                        <td className="px-4 py-3">
                          {u.is_admin ? (
                            <span className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">Admin</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs">User</span>
                          )}
                        </td>
                        {/* Tombol Aksi Reset Password & Upgrade */}
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          {!u.is_admin && (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-[#BE5366] hover:bg-[#9E4053] text-white font-medium"
                              disabled={accountBusyId === u.user_id}
                              onClick={() => upgradeToPremium(u)}
                            >
                              <Sparkles className="h-3.5 w-3.5 mr-1" /> Upgrade Premium
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetPassword(u)}
                            disabled={resettingUserId === u.user_id}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <KeyRound className="h-4 w-4 mr-2" />
                            Reset
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 3: WHATSAPP SERVICE */}
        <TabsContent value="whatsapp">
          <Card className="rounded-2xl p-6" data-testid="admin-wa-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="font-serif text-2xl">WhatsApp Service</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Digunakan untuk mengirim kode OTP lupa password.
                </p>
              </div>
              <div
                data-testid="admin-wa-badge"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                  !wa.serviceUp
                    ? "bg-destructive/10 text-destructive"
                    : wa.connected
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                <Circle className={`h-3 w-3 ${!wa.serviceUp ? "fill-destructive" : wa.connected ? "fill-primary" : ""}`} />
                {!wa.serviceUp
                  ? "Service tidak berjalan"
                  : wa.connected
                    ? `Terhubung${wa.phone ? ` · +${wa.phone}` : ""}`
                    : "Belum terhubung"}
              </div>
            </div>

            {!wa.serviceUp ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
                <p className="font-medium mb-1">wa-service tidak bisa dihubungi.</p>
                <p className="text-muted-foreground">
                  Jalankan <code className="font-mono">cd wa-service &amp;&amp; npm start</code> lalu muat ulang halaman ini.
                </p>
                {wa.error && <p className="text-muted-foreground mt-2 font-mono text-xs break-all">{wa.error}</p>}
              </div>
            ) : wa.connected ? (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
                <p className="font-medium mb-1">WhatsApp siap mengirim OTP.</p>
                <p className="text-muted-foreground">
                  Nomor aktif: <span className="font-mono">+{wa.phone || "tidak diketahui"}</span>
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 py-4">
                {wa.qr ? (
                  <img
                    src={wa.qr}
                    alt="QR pairing WhatsApp"
                    data-testid="admin-wa-qr"
                    className="h-64 w-64 rounded-xl border border-border bg-white p-2"
                  />
                ) : (
                  <div
                    data-testid="admin-wa-qr-pending"
                    className="h-64 w-64 grid place-items-center rounded-xl border border-dashed border-border text-sm text-muted-foreground text-center px-6"
                  >
                    Menunggu QR dari wa-service…
                  </div>
                )}
                <p className="text-sm text-muted-foreground text-center max-w-sm">
                  Di WhatsApp buka <strong>Settings → Linked Devices → Link a Device</strong>, lalu pindai QR di atas.
                </p>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { loadWaStatus(); loadWaQr(); }}
                data-testid="admin-wa-refresh"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Muat Ulang Status
              </Button>
              {!wa.connected && wa.serviceUp && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshWaQr}
                  disabled={waBusy}
                  data-testid="admin-wa-generate-qr"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${waBusy ? "animate-spin" : ""}`} /> Generate QR Baru
                </Button>
              )}
              <div className="flex-1" />
              {wa.connected && !waConfirmLogout && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWaConfirmLogout(true)}
                  data-testid="admin-wa-logout"
                >
                  Ganti Nomor
                </Button>
              )}
              {waConfirmLogout && (
                <div className="flex items-center gap-2" data-testid="admin-wa-logout-confirm">
                  <span className="text-xs text-muted-foreground">Putuskan nomor ini dan pair ulang?</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={waBusy}
                    onClick={waLogout}
                    data-testid="admin-wa-logout-yes"
                  >
                    Ya, Putuskan
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setWaConfirmLogout(false)}>
                    Batal
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* TAB 4: MASA AKTIF AKUN & PEMBAYARAN */}
        <TabsContent value="accounts" className="space-y-6">
          {/* Card Permintaan Pembayaran Premium */}
          <Card className="rounded-2xl overflow-hidden border-primary/20">
            <div className="p-4 bg-primary/5 border-b flex justify-between items-center gap-3 flex-wrap">
              <div>
                <span className="text-sm font-semibold text-primary">Konfirmasi Pembayaran Premium (Rp 45.000 / 1 Thn)</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Daftar transfer bank & QRIS dari user yang meminta verifikasi akun Premium 365 hari.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={loadPayments} disabled={loadingPayments}>
                <Clock className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>
            {loadingPayments ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Memuat data pembayaran...</div>
            ) : payments.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Belum ada permintaan konfirmasi pembayaran.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2.5">User / HP</th>
                      <th className="text-left px-4 py-2.5">Nominal & Metode</th>
                      <th className="text-left px-4 py-2.5">Pengirim / Bank</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                      <th className="text-right px-4 py-2.5">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.payment_id} className="border-t border-border">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-foreground">{p.user_name}</div>
                          <div className="text-[11px] text-muted-foreground font-mono">{p.user_phone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-primary">Rp {Number(p.amount || 45000).toLocaleString("id-ID")}</div>
                          <div className="text-[11px] text-muted-foreground">{p.payment_method} ({p.plan_days || 365} hari)</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{p.sender_name}</div>
                          <div className="text-[11px] text-muted-foreground">{p.sender_bank} {p.notes ? `• ${p.notes}` : ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          {p.status === "approved" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">Setujui</span>
                          ) : p.status === "rejected" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">Ditolak</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">Menunggu</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          {p.status === "pending" ? (
                            <>
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white rounded-full px-3"
                                disabled={verifyingPayId === p.payment_id}
                                onClick={() => handleVerifyPayment(p.payment_id, "approve")}
                              >
                                Verifikasi
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50 rounded-full px-3"
                                disabled={verifyingPayId === p.payment_id}
                                onClick={() => handleVerifyPayment(p.payment_id, "reject")}
                              >
                                Tolak
                              </Button>
                            </>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">Selesai</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="rounded-2xl overflow-hidden" data-testid="admin-accounts-panel">
            <div className="p-4 bg-muted/30 border-b flex justify-between items-center gap-3 flex-wrap">
              <div>
                <span className="text-sm font-medium">Masa Aktif Akun</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  1 tahun aktif &rarr; 3 bulan tenggang &rarr; arsip 30 hari &rarr; hapus permanen.
                  Akun yang diarsipkan masih bisa dipulihkan.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={loadAccounts} disabled={loadingAccounts}>
                <Clock className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>
            {loadingAccounts ? (
              <div className="p-10 text-center text-muted-foreground">Memuat data...</div>
            ) : accounts.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">Belum ada akun.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Nama</th>
                      <th className="text-left px-4 py-3">Nomor HP</th>
                      <th className="text-left px-4 py-3">Paket</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Aktif Sampai</th>
                      <th className="text-left px-4 py-3">Tenggang / Hapus</th>
                      <th className="text-right px-4 py-3">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => {
                      const expired = a.state === "grace" || a.state === "archived" || a.state === "purged";
                      return (
                        <tr key={a.user_id} className="border-t border-border" data-testid="admin-account-row">
                          <td className="px-4 py-3 font-medium">
                            <div>{a.name}</div>
                            {a.partner?.has_partner && (
                              <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                <span className="text-primary font-bold">💍</span> Pasangan: <span className="text-foreground font-medium">{a.partner.partner_name}</span> ({a.partner.partner_phone})
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono">{a.phone}</td>
                          <td className="px-4 py-3">
                            {a.plan_type === "trial" ? (
                              <span className="rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 px-2.5 py-1 text-xs font-medium border border-blue-200 dark:border-blue-800">Trial (14H)</span>
                            ) : (
                              <span className="rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-2.5 py-1 text-xs font-medium border border-emerald-200 dark:border-emerald-800">Premium</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {a.state === "exempt" && (
                              <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">Admin</span>
                            )}
                            {a.state === "active" && (
                              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs text-primary">Aktif</span>
                            )}
                            {a.state === "grace" && (
                              <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs text-amber-700">Masa Tenggang</span>
                            )}
                            {a.state === "archived" && (
                              <span className="rounded-full bg-orange-500/15 px-2.5 py-1 text-xs text-orange-700">Diarsipkan</span>
                            )}
                            {a.state === "purged" && (
                              <span className="rounded-full bg-destructive/15 px-2.5 py-1 text-xs text-destructive">Siap Dihapus</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {(a.plan_expires_at || "").slice(0, 10) || "\u2014"}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {a.state === "grace" && <>Tenggang s.d. {(a.grace_expires_at || "").slice(0, 10)}</>}
                            {a.state === "archived" && <>Dihapus {(a.purge_at || "").slice(0, 10)}</>}
                            {a.state === "purged" && <>Jatuh tempo</>}
                            {a.state === "active" && <>\u2014</>}
                            {a.state === "exempt" && <>Tanpa batas</>}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {a.state === "exempt" ? (
                              <span className="text-xs text-muted-foreground">\u2014</span>
                            ) : expired ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={accountBusyId === a.user_id}
                                onClick={() => restoreAccount(a)}
                                data-testid="admin-account-restore"
                              >
                                Pulihkan
                              </Button>
                            ) : a.plan_type === "trial" ? (
                              <Button
                                size="sm"
                                variant="default"
                                className="bg-[#BE5366] hover:bg-[#9E4053] text-white font-medium"
                                disabled={accountBusyId === a.user_id}
                                onClick={() => upgradeToPremium(a)}
                                data-testid="admin-account-upgrade-premium"
                              >
                                <Sparkles className="h-3.5 w-3.5 mr-1" /> Upgrade ke Premium
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={accountBusyId === a.user_id}
                                onClick={() => extendAccount(a)}
                                data-testid="admin-account-extend"
                              >
                                Perpanjang 1 Tahun
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 5: BANK KUTIPAN */}
        <TabsContent value="quotes">
          <Card className="rounded-2xl p-5 md:p-6" data-testid="admin-quotes-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <h2 className="font-serif text-2xl">Bank Kutipan</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Kutipan yang tampil di &ldquo;Kutipan Hari Ini&rdquo; pada dashboard semua user.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={loadQuotes} disabled={loadingQuotes}>
                <Clock className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>

            <div className="rounded-xl border border-border bg-muted/30 p-4 mb-6">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                {quoteEditId ? "Ubah Kutipan" : "Tambah Kutipan"}
              </Label>
              <Textarea
                value={quoteText}
                onChange={(e) => setQuoteText(e.target.value)}
                placeholder="Tulis kutipan di sini…"
                maxLength={500}
                className="mt-2 min-h-[84px]"
                data-testid="admin-quote-input"
              />
              <div className="mt-3 flex items-center gap-2">
                <Button onClick={saveQuote} disabled={quoteBusy} className="rounded-full" data-testid="admin-quote-save-btn">
                  {quoteEditId ? "Simpan Perubahan" : "Tambah"}
                </Button>
                {quoteEditId && (
                  <Button variant="ghost" className="rounded-full" onClick={resetQuoteForm}>
                    Batal
                  </Button>
                )}
                <span className="text-xs text-muted-foreground ml-auto">{quoteText.length}/500</span>
              </div>
            </div>

            {loadingQuotes ? (
              <div className="py-10 text-center text-muted-foreground">Memuat kutipan...</div>
            ) : quotes.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
                Bank kutipan kosong. Dashboard akan memakai kutipan bawaan sampai ada kutipan di sini.
              </div>
            ) : (
              <div className="space-y-2">
                {quotes.map((q, i) => (
                  <div
                    key={q.quote_id}
                    data-testid="admin-quote-row"
                    className={`flex items-start gap-3 rounded-xl border border-border p-3 ${quoteEditId === q.quote_id ? "ring-1 ring-primary" : ""}`}
                  >
                    <span className="text-xs text-muted-foreground font-mono w-6 shrink-0 pt-0.5">{i + 1}</span>
                    <p className="flex-1 text-sm italic leading-relaxed">{q.text}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Ubah kutipan"
                        onClick={() => { setQuoteEditId(q.quote_id); setQuoteText(q.text); }}
                        data-testid="admin-quote-edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                        title="Hapus kutipan"
                        onClick={() => removeQuote(q)}
                        data-testid="admin-quote-delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 6: LIBRARY MUSIK */}
        <TabsContent value="music">
          <Card className="rounded-2xl p-5 md:p-6" data-testid="admin-music-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <h2 className="font-serif text-2xl">Library Musik</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Lagu yang bisa dipilih semua pasangan saat menyusun Undangan Digital.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={loadTracks} disabled={loadingTracks}>
                <Clock className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>

            <div className="mb-5 rounded-lg border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
              <p className="mb-1 font-medium text-foreground">Sumber file gratis &amp; berlisensi</p>
              <p>Unduh dari salah satu situs berikut, lalu unggah di sini:</p>
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                <li><strong>Incompetech</strong> (Kevin MacLeod) - CC BY 4.0, wajib tulis kredit</li>
                <li><strong>Free Music Archive</strong> - lisensi per track, cek satu per satu</li>
                <li><strong>ccMixter</strong> - CC BY / CC BY-SA, wajib kredit + tautan</li>
                <li><strong>Pixabay Music</strong> - Pixabay License, tanpa syarat kredit</li>
                <li><strong>Jamendo</strong> - filter &ldquo;Free&rdquo; untuk yang berlisensi CC</li>
              </ul>
              <p className="mt-1.5">Hindari CC BY-NC untuk keperluan komersial.</p>
            </div>

            <div className="rounded-xl border border-border bg-muted/30 p-4 mb-6">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Tambah Lagu
              </Label>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">Judul *</span>
                  <Input
                    value={trackForm.title}
                    onChange={(e) => setTrackField("title", e.target.value)}
                    placeholder="Aku Cinta Kamu"
                    maxLength={160}
                    data-testid="admin-track-title"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">Artis / Penyanyi</span>
                  <Input
                    value={trackForm.artist}
                    onChange={(e) => setTrackField("artist", e.target.value)}
                    placeholder="Kunto Aji"
                    maxLength={160}
                    data-testid="admin-track-artist"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">Kredit *</span>
                  <Input
                    value={trackForm.credit}
                    onChange={(e) => setTrackField("credit", e.target.value)}
                    placeholder="Musik: Kevin MacLeod (incompetech.com), CC BY"
                    maxLength={300}
                    data-testid="admin-track-credit"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">Jenis Lisensi *</span>
                  <Input
                    value={trackForm.license}
                    onChange={(e) => setTrackField("license", e.target.value)}
                    placeholder="CC BY 4.0"
                    maxLength={60}
                    data-testid="admin-track-license"
                  />
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-1.5">
                <span className="text-xs text-muted-foreground">File Audio * (MP3/WAV/OGG/M4A, maks 8 MB)</span>
                <input
                  type="file"
                  accept="audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/aac"
                  onChange={(e) => setTrackFile(e.target.files?.[0] || null)}
                  className="text-xs"
                  data-testid="admin-track-file"
                />
                {trackPratinjauUrl && (
                  <audio
                    src={trackPratinjauUrl}
                    controls
                    className="mt-1 h-9 w-full max-w-sm"
                    aria-label="Pratinjau lagu"
                  />
                )}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <Button
                  onClick={saveTrack}
                  disabled={trackBusy}
                  className="rounded-full"
                  data-testid="admin-track-save-btn"
                >
                  {trackBusy ? "Mengunggah..." : "Tambah ke Library"}
                </Button>
                <Button variant="ghost" className="rounded-full" onClick={resetTrackForm}>
                  Reset
                </Button>
              </div>
            </div>

            {loadingTracks ? (
              <div className="py-10 text-center text-muted-foreground">Memuat library musik...</div>
            ) : tracks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
                Library kosong. Unggah lagu agar pasangan bisa memilihnya saat menyusun undangan.
              </div>
            ) : (
              <div className="space-y-2">
                {tracks.map((t) => (
                  <div
                    key={t.track_id}
                    data-testid="admin-track-row"
                    className="flex flex-col gap-2 rounded-xl border border-border p-3 sm:flex-row sm:items-center"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{t.title}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {t.artist ? `${t.artist} - ` : ""}
                        {t.license}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">{t.credit}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <audio
                        src={`${API_BASE}/public/tracks/${t.track_id}`}
                        controls
                        preload="none"
                        className="h-8 w-40"
                        aria-label={`Pratinjau ${t.title}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                        title="Hapus lagu"
                        onClick={() => deleteTrack(t.track_id)}
                        data-testid="admin-track-delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 7: FEEDBACK & RATING */}
        <TabsContent value="feedback">
          <div className="space-y-6">
            {/* STATS SUMMARY */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="p-5 rounded-2xl flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
                  <Star className="h-6 w-6 fill-amber-500 text-amber-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold font-serif">{feedbackStats.avg_rating} <span className="text-sm font-normal text-muted-foreground">/ 5.0</span></div>
                  <div className="text-xs text-muted-foreground">Rata-Rata Rating</div>
                </div>
              </Card>

              <Card className="p-5 rounded-2xl flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold font-serif">{feedbackStats.total_count}</div>
                  <div className="text-xs text-muted-foreground">Total Feedback Masuk</div>
                </div>
              </Card>

              <Card className="p-4 rounded-2xl flex flex-col justify-center">
                <div className="text-xs font-medium text-muted-foreground mb-2">Sebaran Rating:</div>
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const cnt = feedbackStats.rating_counts?.[s] || 0;
                    const pct = feedbackStats.total_count > 0 ? (cnt / feedbackStats.total_count) * 100 : 0;
                    return (
                      <div key={s} className="flex items-center gap-2 text-xs">
                        <span className="w-4 font-mono font-medium">{s}★</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-6 text-right text-muted-foreground font-mono">{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* CONTROLS (SEARCH & FILTER) */}
            <Card className="p-4 rounded-2xl">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cari pengguna, email, atau ulasan..."
                    value={feedbackSearch}
                    onChange={(e) => setFeedbackSearch(e.target.value)}
                    className="pl-9 rounded-xl"
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <select
                    value={feedbackRatingFilter}
                    onChange={(e) => setFeedbackRatingFilter(e.target.value)}
                    className="h-10 px-3 py-2 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">Semua Bintang</option>
                    <option value="5">5 Bintang (⭐⭐⭐⭐⭐)</option>
                    <option value="4">4 Bintang (⭐⭐⭐⭐)</option>
                    <option value="3">3 Bintang (⭐⭐⭐)</option>
                    <option value="2">2 Bintang (⭐⭐)</option>
                    <option value="1">1 Bintang (⭐)</option>
                  </select>

                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl shrink-0"
                    onClick={loadFeedbacks}
                    disabled={loadingFeedbacks}
                    title="Refresh Data"
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingFeedbacks ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </div>
            </Card>

            {/* FEEDBACK LIST TABLE */}
            <Card className="rounded-2xl overflow-hidden">
              {loadingFeedbacks ? (
                <div className="p-12 text-center text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />
                  <span>Memuat daftar ulasan...</span>
                </div>
              ) : feedbacks.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <span>Belum ada feedback atau ulasan dari user.</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground border-b">
                      <tr>
                        <th className="p-4 text-left font-medium">Pengguna</th>
                        <th className="p-4 text-left font-medium">Rating</th>
                        <th className="p-4 text-left font-medium">Ulasan / Masukan</th>
                        <th className="p-4 text-left font-medium">Tanggal</th>
                        <th className="p-4 text-right font-medium">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {feedbacks
                        .filter((fb) => {
                          const matchSearch =
                            !feedbackSearch ||
                            fb.user_name?.toLowerCase().includes(feedbackSearch.toLowerCase()) ||
                            fb.user_email?.toLowerCase().includes(feedbackSearch.toLowerCase()) ||
                            fb.comment?.toLowerCase().includes(feedbackSearch.toLowerCase());
                          const matchRating =
                            feedbackRatingFilter === "all" ||
                            String(fb.rating) === feedbackRatingFilter;
                          return matchSearch && matchRating;
                        })
                        .map((fb) => (
                          <tr key={fb.feedback_id} className="hover:bg-muted/30 transition-colors">
                            <td className="p-4">
                              <div className="font-medium text-foreground">{fb.user_name || "User"}</div>
                              <div className="text-xs text-muted-foreground font-mono">{fb.user_email || "-"}</div>
                            </td>
                            <td className="p-4 whitespace-nowrap">
                              <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star
                                    key={s}
                                    className={`h-4 w-4 ${
                                      s <= fb.rating
                                        ? "fill-amber-400 text-amber-400"
                                        : "text-muted-foreground/20"
                                    }`}
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="p-4 max-w-md">
                              <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                                {fb.comment || <span className="text-muted-foreground italic text-xs">Tanpa pesan ulasan</span>}
                              </p>
                            </td>
                            <td className="p-4 text-xs text-muted-foreground whitespace-nowrap font-mono">
                              {formatDate(fb.created_at)}
                            </td>
                            <td className="p-4 text-right whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
                                onClick={() => deleteFeedback(fb.feedback_id)}
                                disabled={deletingFeedbackId === fb.feedback_id}
                                title="Hapus Ulasan"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* TAB HELPDESK & LAPORAN TIKET */}
        <TabsContent value="helpdesk">
          <div className="space-y-6">
            {/* STATS SUMMARY HELPDESK */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <Card className="p-4 rounded-2xl flex items-center gap-3.5">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <LifeBuoy className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xl font-bold font-serif">{helpdeskStats.total}</div>
                  <div className="text-xs text-muted-foreground">Total Tiket</div>
                </div>
              </Card>

              <Card className="p-4 rounded-2xl flex items-center gap-3.5">
                <div className="h-11 w-11 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center shrink-0">
                  <Bug className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xl font-bold font-serif">{helpdeskStats.category_counts?.bug || 0}</div>
                  <div className="text-xs text-muted-foreground">Laporan Bug/Error</div>
                </div>
              </Card>

              <Card className="p-4 rounded-2xl flex items-center gap-3.5">
                <div className="h-11 w-11 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xl font-bold font-serif">{helpdeskStats.category_counts?.suggestion || 0}</div>
                  <div className="text-xs text-muted-foreground">Saran & Masukan</div>
                </div>
              </Card>

              <Card className="p-4 rounded-2xl flex items-center gap-3.5">
                <div className="h-11 w-11 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xl font-bold font-serif">{helpdeskStats.status_counts?.resolved || 0}</div>
                  <div className="text-xs text-muted-foreground">Tiket Selesai</div>
                </div>
              </Card>
            </div>

            {/* CONTROLS (SEARCH & FILTER) */}
            <Card className="p-4 rounded-2xl">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cari pelapor, email, nomor HP, atau subjek..."
                    value={helpdeskSearch}
                    onChange={(e) => setHelpdeskSearch(e.target.value)}
                    className="pl-9 rounded-xl"
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <select
                    value={helpdeskCategoryFilter}
                    onChange={(e) => setHelpdeskCategoryFilter(e.target.value)}
                    className="h-10 px-3 py-2 rounded-xl border bg-background text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">Semua Kategori</option>
                    <option value="bug">🐛 Bug / Error</option>
                    <option value="suggestion">💡 Saran</option>
                    <option value="help">❓ Bantuan</option>
                  </select>

                  <select
                    value={helpdeskStatusFilter}
                    onChange={(e) => setHelpdeskStatusFilter(e.target.value)}
                    className="h-10 px-3 py-2 rounded-xl border bg-background text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">Semua Status</option>
                    <option value="open">Baru (Open)</option>
                    <option value="in_progress">Diproses</option>
                    <option value="resolved">Selesai</option>
                  </select>

                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl shrink-0"
                    onClick={loadHelpdeskTickets}
                    disabled={loadingHelpdesk}
                    title="Refresh Tiket"
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingHelpdesk ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </div>
            </Card>

            {/* TICKETS LIST TABLE */}
            <Card className="rounded-2xl overflow-hidden">
              {loadingHelpdesk ? (
                <div className="p-12 text-center text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />
                  <span>Memuat daftar tiket helpdesk...</span>
                </div>
              ) : helpdeskTickets.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <LifeBuoy className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <span>Belum ada tiket atau laporan masalah dari pengguna.</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground border-b">
                      <tr>
                        <th className="p-4 text-left font-medium">Pelapor</th>
                        <th className="p-4 text-left font-medium">Kategori</th>
                        <th className="p-4 text-left font-medium">Masalah / Subjek</th>
                        <th className="p-4 text-left font-medium">Status</th>
                        <th className="p-4 text-left font-medium">Tanggal</th>
                        <th className="p-4 text-right font-medium">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {helpdeskTickets
                        .filter((t) => {
                          const matchSearch =
                            !helpdeskSearch ||
                            t.user_name?.toLowerCase().includes(helpdeskSearch.toLowerCase()) ||
                            t.user_email?.toLowerCase().includes(helpdeskSearch.toLowerCase()) ||
                            t.user_phone?.includes(helpdeskSearch) ||
                            t.subject?.toLowerCase().includes(helpdeskSearch.toLowerCase()) ||
                            t.description?.toLowerCase().includes(helpdeskSearch.toLowerCase());
                          const matchCat =
                            helpdeskCategoryFilter === "all" || t.category === helpdeskCategoryFilter;
                          const matchSt =
                            helpdeskStatusFilter === "all" || t.status === helpdeskStatusFilter;
                          return matchSearch && matchCat && matchSt;
                        })
                        .map((t) => {
                          const catBadge = {
                            bug: { label: "Bug / Error", color: "bg-rose-50 text-rose-700 border-rose-200" },
                            suggestion: { label: "Saran", color: "bg-amber-50 text-amber-700 border-amber-200" },
                            help: { label: "Bantuan", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
                          }[t.category] || { label: t.category, color: "bg-muted text-muted-foreground" };

                          const statusBadge = {
                            open: { label: "Baru", color: "bg-blue-50 text-blue-700 border-blue-200" },
                            in_progress: { label: "Diproses", color: "bg-amber-50 text-amber-700 border-amber-200" },
                            resolved: { label: "Selesai", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                          }[t.status] || { label: t.status, color: "bg-muted text-muted-foreground" };

                          return (
                            <tr key={t.ticket_id} className="hover:bg-muted/30 transition-colors">
                              <td className="p-4">
                                <div className="font-medium text-foreground">{t.user_name || "User"}</div>
                                <div className="text-xs text-muted-foreground font-mono">{t.user_email || t.user_phone || "-"}</div>
                                {t.user_phone && (
                                  <a
                                    href={`https://wa.me/${t.user_phone.replace(/^0/, "62")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
                                  >
                                    Chat via WA
                                  </a>
                                )}
                              </td>
                              <td className="p-4 whitespace-nowrap">
                                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${catBadge.color}`}>
                                  {catBadge.label}
                                </span>
                              </td>
                              <td className="p-4 max-w-sm">
                                <div className="font-medium text-foreground text-sm">{t.subject}</div>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                                  {t.description}
                                </p>
                                {t.page_url && (
                                  <div className="text-[10px] text-muted-foreground/80 mt-1 font-mono">
                                    Halaman: {t.page_url}
                                  </div>
                                )}
                                {t.admin_note && (
                                  <div className="text-[11px] text-amber-800 bg-amber-50/80 p-1.5 rounded border border-amber-200/60 mt-1.5">
                                    <strong>Catatan Admin:</strong> {t.admin_note}
                                  </div>
                                )}
                              </td>
                              <td className="p-4 whitespace-nowrap">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusBadge.color}`}>
                                  {statusBadge.label}
                                </span>
                              </td>
                              <td className="p-4 text-xs text-muted-foreground whitespace-nowrap font-mono">
                                {formatDate(t.created_at)}
                              </td>
                              <td className="p-4 text-right whitespace-nowrap space-x-1">
                                {t.status !== "resolved" ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs rounded-lg text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => updateTicketStatus(t.ticket_id, "resolved")}
                                    disabled={updatingTicketId === t.ticket_id}
                                  >
                                    Tandai Selesai
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs rounded-lg text-amber-600 border-amber-300 hover:bg-amber-50"
                                    onClick={() => updateTicketStatus(t.ticket_id, "in_progress")}
                                    disabled={updatingTicketId === t.ticket_id}
                                  >
                                    Buka Kembali
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                  onClick={() => deleteHelpdeskTicket(t.ticket_id)}
                                  title="Hapus Tiket"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* TAB ANALITIK USER */}
        <TabsContent value="analytics">
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-serif font-bold text-foreground">Analitik Aktivitas Pengguna</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Ringkasan statistik real-time, jam aktif tersibuk, dan durasi rata-rata sesi pengguna.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadAnalytics}
                disabled={loadingAnalytics}
                className="gap-2 self-start sm:self-auto"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingAnalytics ? "animate-spin" : ""}`} />
                <span>Refresh Analytics</span>
              </Button>
            </div>

            {/* 4 KPI Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Card 1: User Online */}
              <Card className="p-5 rounded-2xl border border-border shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-muted-foreground mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">User Realtime</span>
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold font-serif text-foreground">
                    {analytics.summary.online_realtime}
                  </span>
                  <span className="text-xs text-emerald-600 font-semibold">Online (15 Min)</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Pengguna aktif di platform saat ini.
                </p>
              </Card>

              {/* Card 2: Average Spend Time */}
              <Card className="p-5 rounded-2xl border border-border shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-muted-foreground mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Avg Spend Time</span>
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold font-serif text-foreground">
                    {analytics.summary.avg_spend_time_minutes}
                  </span>
                  <span className="text-xs text-muted-foreground font-semibold">Menit / Sesi</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Rata-rata durasi keaktifan per sesi pengguna.
                </p>
              </Card>

              {/* Card 3: Peak Activity Hour */}
              <Card className="p-5 rounded-2xl border border-border shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-muted-foreground mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Jam Tersibuk</span>
                  <Zap className="h-4 w-4 text-amber-500" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold font-serif text-foreground">
                    {analytics.summary.peak_activity_hour}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Waktu puncak lalu lintas keaktifan user.
                </p>
              </Card>

              {/* Card 4: Daily Active Users (DAU) */}
              <Card className="p-5 rounded-2xl border border-border shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-muted-foreground mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Daily Active (DAU)</span>
                  <TrendingUp className="h-4 w-4 text-blue-500" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold font-serif text-foreground">
                    {analytics.summary.daily_active_users}
                  </span>
                  <span className="text-xs text-muted-foreground">({analytics.summary.weekly_active_users} WAU)</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  User unik yang aktif dalam 24 jam terakhir.
                </p>
              </Card>
            </div>

            {/* Account Breakdown Summary Grid */}
            <Card className="p-6 rounded-2xl border border-border shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>Ringkasan Akun Terdaftar ({analytics.summary.total_accounts} Total)</span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-1">
                <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-center">
                  <p className="text-[11px] text-muted-foreground font-semibold">Aktif (Approved)</p>
                  <p className="text-xl font-bold text-foreground mt-1">{analytics.summary.approved_count}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-center">
                  <p className="text-[11px] text-muted-foreground font-semibold">Menunggu (Pending)</p>
                  <p className="text-xl font-bold text-amber-600 mt-1">{analytics.summary.pending_count}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-center">
                  <p className="text-[11px] text-muted-foreground font-semibold">Akun Premium</p>
                  <p className="text-xl font-bold text-primary mt-1">{analytics.summary.premium_count}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-center">
                  <p className="text-[11px] text-muted-foreground font-semibold">Akun Trial 14-Hari</p>
                  <p className="text-xl font-bold text-foreground mt-1">{analytics.summary.trial_count}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-center">
                  <p className="text-[11px] text-muted-foreground font-semibold">Akun Admin</p>
                  <p className="text-xl font-bold text-blue-600 mt-1">{analytics.summary.admin_count}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-muted/40 border border-border/50 text-center">
                  <p className="text-[11px] text-muted-foreground font-semibold">Pasangan (User biasa)</p>
                  <p className="text-xl font-bold text-foreground mt-1">{Math.max(0, analytics.summary.approved_count - analytics.summary.admin_count)}</p>
                </div>
              </div>
            </Card>

            {/* Peak Activity Hours 24-Hour Bar Chart */}
            <Card className="p-6 rounded-2xl border border-border shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-serif font-bold text-foreground flex items-center gap-2">
                    <BarChart2 className="h-4 w-4 text-primary" />
                    <span>Distribusi Jam Aktif User (24-Hour Peak Activity)</span>
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      📅 {analytics.date_label || "Hari Ini"}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Zona Waktu: <strong>WIB (UTC+7)</strong>
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Period Filter Buttons */}
                  <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-xl border border-border">
                    <button
                      type="button"
                      onClick={() => handlePeriodChange("today")}
                      disabled={loadingAnalytics}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                        analyticsPeriod === "today"
                          ? "bg-background text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Hari Ini (WIB)
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePeriodChange("7d")}
                      disabled={loadingAnalytics}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                        analyticsPeriod === "7d"
                          ? "bg-background text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      7 Hari Terakhir
                    </button>
                  </div>

                  <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary"></span>
                    <span>Jam Sibuk</span>
                  </div>
                </div>
              </div>

              {/* Chart Visualizer */}
              <div className="pt-6 pb-2">
                {analytics.hourly_activity && analytics.hourly_activity.length > 0 ? (
                  (() => {
                    const maxCount = Math.max(...analytics.hourly_activity.map((h) => h.count), 1);
                    return (
                      <div className="space-y-4">
                        <div className="h-48 flex items-end gap-1.5 sm:gap-2 px-2 border-b border-border">
                          {analytics.hourly_activity.map((item, idx) => {
                            const pct = Math.max(8, Math.round((item.count / maxCount) * 100));
                            const isMax = item.count === maxCount && maxCount > 0;
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center gap-1 group relative h-full justify-end">
                                {/* Tooltip hover */}
                                <div className="absolute -top-9 hidden group-hover:flex flex-col items-center z-10 pointer-events-none">
                                  <span className="bg-popover text-popover-foreground text-[10px] font-semibold px-2 py-1 rounded shadow border border-border whitespace-nowrap">
                                    {item.hour} WIB: {item.count} Sesi
                                  </span>
                                </div>
                                <div
                                  className={`w-full rounded-t-md transition-all duration-300 ${
                                    isMax ? "bg-primary shadow-sm ring-1 ring-primary" : item.count > 0 ? "bg-primary/60 group-hover:bg-primary" : "bg-muted/50"
                                  }`}
                                  style={{ height: `${pct}%` }}
                                ></div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between text-[10px] sm:text-xs text-muted-foreground font-mono px-1">
                          <span>00:00</span>
                          <span>04:00</span>
                          <span>08:00</span>
                          <span>12:00</span>
                          <span>16:00</span>
                          <span>20:00</span>
                          <span>23:00</span>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-xs">
                    Memuat data grafik analitik...
                  </div>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* TAB LOG SYSTEM ERROR */}
        <TabsContent value="logs">
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-serif font-bold text-foreground">Log System &amp; Error Application</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Catatan error otomatis dari Frontend (UI/JS) dan Backend (FastAPI).
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadErrorLogs}
                  disabled={loadingLogs}
                  className="gap-2"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loadingLogs ? "animate-spin" : ""}`} />
                  <span>Refresh Log</span>
                </Button>
                {errorLogs.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={clearErrorLogs}
                    className="gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Hapus Semua Log</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cari pesan error, route, nama user, atau stack trace..."
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  className="pl-9 text-xs"
                />
              </div>
              <select
                value={logSourceFilter}
                onChange={(e) => setLogSourceFilter(e.target.value)}
                className="h-9 px-3 rounded-md border border-input bg-background text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="all">Semua Sumber Error</option>
                <option value="frontend">Frontend Only (JS / UI)</option>
                <option value="backend">Backend Only (FastAPI 500)</option>
              </select>
            </div>

            {/* Table Log Error */}
            <Card className="rounded-2xl overflow-hidden border border-border shadow-sm">
              {errorLogs.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground flex flex-col items-center gap-2">
                  <Check className="h-8 w-8 text-emerald-500 opacity-60" />
                  <span className="font-semibold text-foreground">Sistem Berjalan Mulus!</span>
                  <span className="text-xs">Belum ada catatan error yang terdeteksi di aplikasi.</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-wider text-[11px]">
                      <tr>
                        <th className="text-left p-3.5 font-semibold">Waktu</th>
                        <th className="text-left p-3.5 font-semibold">Sumber</th>
                        <th className="text-left p-3.5 font-semibold">Pesan Error</th>
                        <th className="text-left p-3.5 font-semibold">Path / Route</th>
                        <th className="text-left p-3.5 font-semibold">User</th>
                        <th className="text-right p-3.5 font-semibold">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {errorLogs
                        .filter((l) => {
                          if (logSourceFilter !== "all") {
                            if (logSourceFilter === "frontend" && !l.source.startsWith("frontend")) return false;
                            if (logSourceFilter === "backend" && l.source !== "backend") return false;
                          }
                          if (!logSearch.trim()) return true;
                          const q = logSearch.toLowerCase();
                          return (
                            (l.message || "").toLowerCase().includes(q) ||
                            (l.path || "").toLowerCase().includes(q) ||
                            (l.user_name || "").toLowerCase().includes(q) ||
                            (l.stack_trace || "").toLowerCase().includes(q)
                          );
                        })
                        .map((l) => {
                          const isBackend = l.source === "backend";
                          return (
                            <tr key={l.log_id} className="hover:bg-muted/40 transition-colors">
                              <td className="p-3.5 font-mono text-[11px] whitespace-nowrap text-muted-foreground">
                                {formatDate(l.created_at)}
                              </td>
                              <td className="p-3.5 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                    isBackend
                                      ? "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20"
                                      : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                                  }`}
                                >
                                  {isBackend ? "Backend" : "Frontend"}
                                </span>
                              </td>
                              <td className="p-3.5 max-w-xs sm:max-w-md truncate font-medium text-foreground">
                                {l.message || "Error tanpa pesan"}
                              </td>
                              <td className="p-3.5 font-mono text-[11px] text-muted-foreground whitespace-nowrap max-w-[150px] truncate">
                                {l.path || "-"}
                              </td>
                              <td className="p-3.5 whitespace-nowrap">
                                {l.user_name ? (
                                  <span className="font-semibold text-foreground">{l.user_name}</span>
                                ) : (
                                  <span className="text-muted-foreground italic">Guest / Unauth</span>
                                )}
                              </td>
                              <td className="p-3.5 text-right whitespace-nowrap">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs font-semibold text-primary"
                                  onClick={() => setSelectedLog(l)}
                                >
                                  Lihat Detail
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Detail Log Error */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => { if (!open) setSelectedLog(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-lg font-serif font-bold flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                  selectedLog?.source === "backend"
                    ? "bg-rose-500/10 text-rose-600 border border-rose-500/20"
                    : "bg-amber-500/10 text-amber-600 border border-amber-500/20"
                }`}
              >
                {selectedLog?.source === "backend" ? "Backend 500" : "Frontend JS"}
              </span>
              <span>Detail Log System Error</span>
            </DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 overflow-y-auto pr-1 text-xs">
              <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl bg-muted/50 border border-border">
                <div>
                  <p className="text-muted-foreground font-semibold">Waktu Kejadian:</p>
                  <p className="font-mono text-foreground mt-0.5">{formatDate(selectedLog.created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-semibold">Route / Endpoint Path:</p>
                  <p className="font-mono text-foreground mt-0.5">{selectedLog.path || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-semibold">User Pelapor:</p>
                  <p className="font-semibold text-foreground mt-0.5">{selectedLog.user_name || "Guest / Anonymous"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-semibold">Sumber Error:</p>
                  <p className="font-mono text-foreground mt-0.5">{selectedLog.source}</p>
                </div>
              </div>

              <div className="space-y-1">
                <p className="font-bold text-foreground">Pesan Error:</p>
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive font-mono text-xs break-words border border-destructive/20">
                  {selectedLog.message}
                </div>
              </div>

              {selectedLog.stack_trace && (
                <div className="space-y-1">
                  <p className="font-bold text-foreground">Stack Trace &amp; Component Callstack:</p>
                  <pre className="p-3 rounded-lg bg-slate-950 text-slate-100 font-mono text-[11px] overflow-x-auto whitespace-pre-wrap max-h-56 leading-relaxed border">
                    {selectedLog.stack_trace}
                  </pre>
                </div>
              )}

              {selectedLog.user_agent && (
                <div className="space-y-1">
                  <p className="font-bold text-foreground">User Agent (Browser &amp; OS):</p>
                  <p className="p-2 rounded bg-muted font-mono text-[11px] text-muted-foreground break-all">
                    {selectedLog.user_agent}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog hasil reset password — password bisa di-copy */}
      <Dialog open={!!resetResult} onOpenChange={(open) => { if (!open) setResetResult(null); }}>
        <DialogContent className="max-w-md" data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Password Berhasil Di-reset</DialogTitle>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Password baru untuk <span className="font-medium text-foreground">{resetResult.name}</span>
                {resetResult.phone && <> (<span className="font-mono">{resetResult.phone}</span>)</>}:
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="admin-generated-password"
                  className="flex-1 rounded-lg bg-muted px-3 py-2.5 font-mono text-base tracking-wide select-all break-all"
                >
                  {resetResult.password}
                </code>
                <Button
                  type="button"
                  size="icon"
                  variant={copied ? "default" : "outline"}
                  onClick={copyResetPassword}
                  data-testid="admin-copy-password-btn"
                  aria-label="Salin password"
                  title="Salin password"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Segera sampaikan password ini ke user melalui kanal yang aman. User harus login ulang, dan disarankan segera mengganti password di menu Settings → Keamanan.
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={copyResetPassword}>
                  {copied ? "Tersalin!" : "Copy"}
                </Button>
                <Button type="button" onClick={() => setResetResult(null)}>Selesai</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}